import React from 'react';

import { ProductItem } from './list-items/product';
import { BrandItem } from './list-items/brand';
import { ProductCollectionItem } from './list-items/product-collection';
import { HistoryItem, Title as HistoryTitle } from './list-items/history-item';
import { KeywordItem } from './list-items/keyword';
import { ShopItem } from './list-items/shop';

import { useAppContext } from '@prisjakt/ui-app-context';
import { extractFilter } from '../filter-helpers';

import { NewSuggestion, ClickHandler, SUGGESTION_ORDER } from '../types';

type Props = {
  items: NewSuggestion[];
  query: string;
  handleClick: ClickHandler;
  selectedId: number;
  handleClearHistory: () => void;
  handleDeleteHistory: (
    e: React.MouseEvent<HTMLButtonElement>,
    historyQuery: string
  ) => void;
  onExtendSearch: (s: NewSuggestion) => void;
  products: Array<{
    id: number;
    price: number;
  }>;
};

export const SuggestionItems = (props: Props) => {
  const {
    handleClearHistory,
    handleClick,
    handleDeleteHistory,
    items,
    onExtendSearch,
    query,
    selectedId,
    products,
  } = props;
  const { t } = useAppContext();

  return (
    <>
      {items.map((suggestion, index) => {
        switch (suggestion.__typename) {
          case 'SuggestedSearchPhrase': {
            if (!query.length) return null;

            return (
              <KeywordItem
                key={`${suggestion.text}-${index.toString()}`}
                text={suggestion.text}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                onClick={() => handleClick(suggestion)}
                onExtendSearch={() => onExtendSearch(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'SuggestedProductCollection': {
            if (!suggestion.path || !query.length) return null;

            const { text } = extractFilter(suggestion);

            return (
              <ProductCollectionItem
                key={`${suggestion.id}-${index.toString()}`}
                id={suggestion.id}
                text={text || suggestion.text}
                brands={suggestion.topBrands}
                path={suggestion.path}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                onClick={() => handleClick(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'SuggestedVirtualCategory':
          case 'SuggestedCategory': {
            if (!query.length) return null;

            return (
              <ProductCollectionItem
                key={`${suggestion.id}-${index.toString()}`}
                id={suggestion.id}
                text={suggestion.text}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                path=""
                onClick={() => handleClick(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'SuggestedBrand': {
            if (!query.length) return null;

            return (
              <BrandItem
                key={`${suggestion.id}-${index.toString()}`}
                id={suggestion.id}
                text={suggestion.text}
                categories={suggestion.topCategories}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                onClick={() => handleClick(suggestion)}
                onExtendSearch={() => onExtendSearch(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'SuggestedProduct': {
            if (!query.length) return null;

            const price = products.find(p => p.id === Number(suggestion.id))
              ?.price;

            return (
              <ProductItem
                key={`${suggestion.id}-${index.toString()}`}
                id={suggestion.id}
                text={suggestion.text}
                price={price}
                category={suggestion.category}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                onClick={() => handleClick(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'SuggestedShop': {
            if (!query.length) return null;

            return (
              <ShopItem
                key={`${suggestion.id}-${index.toString()}`}
                id={suggestion.id}
                text={suggestion.text}
                order={SUGGESTION_ORDER[suggestion.__typename]}
                onClick={() => handleClick(suggestion)}
                query={query}
                selected={selectedId === suggestion.navId}
              />
            );
          }

          case 'history': {
            const firstHistoryItem = items?.findIndex(
              item => item.__typename === 'history'
            );

            return (
              <>
                {firstHistoryItem === index && (
                  <HistoryTitle key="history=title">
                    {t('search', 'search.recent-searches')}
                  </HistoryTitle>
                )}

                <HistoryItem
                  key={`${suggestion.text}-${index + 100}`}
                  selected={suggestion.navId === selectedId}
                  onClick={() => handleClick(suggestion)}
                  onDelete={(e: React.MouseEvent<HTMLButtonElement>) =>
                    handleDeleteHistory(e, suggestion.text)
                  }
                  onClear={handleClearHistory}
                  text={suggestion.text}
                  showClear={
                    index === firstHistoryItem &&
                    items.filter(s => s.__typename === 'history').length > 2
                  }
                />
              </>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
